import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Collapse, Group, Text, TypographyStylesProvider } from '@mantine/core';
import { IconSteeringWheel } from '@tabler/icons-react';
import { stripUnsafeHtml } from '@/lib/sanitize';
import { contactVars, fillPlaceholders, useCopy } from '@/lib/settings';
import { Callout } from '@/components/ui/Callout';
import { StatusPill } from '@/components/ui/StatusPill';

/** The declaration HTML (copy key driver.declaration), script-stripped like the privacy notice. */
export function DriverDeclarationText() {
  const { t, settings } = useCopy();
  const html = stripUnsafeHtml(fillPlaceholders(t('driver.declaration'), contactVars(settings, t('app.name'))));
  return (
    <TypographyStylesProvider fz="md" className="rides-legal">
      {/* Admin-authored HTML; <script>/<iframe>/on* handlers removed in stripUnsafeHtml. */}
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </TypographyStylesProvider>
  );
}

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Already accepted for the current text version, show a compact confirmation instead. */
  accepted?: boolean;
  id?: string;
  /** The API said it is missing (declaration_required): show the reason and scroll here. */
  highlight?: boolean;
  disabled?: boolean;
}

/** Driver declaration (driver.declaration_title + driver.declaration) with its agree checkbox. */
export function DriverDeclarationCard({ checked, onChange, accepted, id, highlight, disabled }: Props) {
  const { t } = useCopy();
  const [reread, setReread] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [highlight]);
  const title = t('driver.declaration_title');

  if (accepted) {
    return (
      <div className="rides-card" id={id} ref={ref} style={{ padding: 16, scrollMarginTop: 80 }}>
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap={10} wrap="nowrap" style={{ minWidth: 0 }}>
            <IconSteeringWheel size={20} color="var(--rides-primary)" aria-hidden="true" style={{ flex: '0 0 auto' }} />
            <Text size="sm" fw={600}>{title}</Text>
          </Group>
          <StatusPill tone="ok">Agreed</StatusPill>
        </Group>
        <Button variant="subtle" px={6} mt={4} onClick={() => setReread((v) => !v)} aria-expanded={reread}>
          {reread ? 'Hide the declaration' : 'Read it again'}
        </Button>
        <Collapse in={reread}>
          <div style={{ marginTop: 8 }}><DriverDeclarationText /></div>
        </Collapse>
      </div>
    );
  }

  return (
    <div className="rides-card" id={id} ref={ref} style={{ padding: 16, scrollMarginTop: 80 }} role="group" aria-labelledby={id ? `${id}-title` : undefined}>
      <Group gap={10} wrap="nowrap" mb={6}>
        <IconSteeringWheel size={20} color="var(--rides-primary)" aria-hidden="true" style={{ flex: '0 0 auto' }} />
        <Text fw={600} id={id ? `${id}-title` : undefined}>{title}</Text>
      </Group>
      {highlight && <div style={{ marginBottom: 8 }}><Callout tone="warn" live>{t('driver.declaration_required')}</Callout></div>}
      <DriverDeclarationText />
      <Checkbox
        mt="sm"
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        disabled={disabled}
        required
        styles={{ body: { alignItems: 'flex-start' }, label: { paddingInlineStart: 10 } }}
        label={<Text size="md" component="span">I have read the driver declaration and I agree to it.</Text>}
      />
    </div>
  );
}
